var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isRenderableType, Renderable } from "../../rendering/renderable/Renderable";
import { TrainingVideoFacet } from "flavr-common";
import { Mesh, MeshBasicMaterial, ShapeGeometry, VideoTexture, DoubleSide, Audio } from "three";
import { MeshRenderable } from "../../rendering/mesh/MeshRenderable";
import { camera } from "../../rendering/camera";
import { Vector3 } from "flavr-math";
import { TrainingVideoSystem } from "./TrainingVideoSystem";
import { RenderOrder } from "../../rendering/RenderOrder";
import { roundedRect } from "../../rendering/speechBubble/speechBubbleGeometry";
const VIDEO_HEIGHT = 0.5;
const VIDEO_CORNER_RADIUS = 0.025;
const VIDEO_OUTLINE_WIDTH = 0.01;
const VIDEO_OUTLINE_COLOR = 0x111827;
export class TrainingVideoRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.type = TrainingVideoRenderable.typestatic;
        this.bucketName = "video";
        this.videoFileName = "video.mp4";
        this.audioFromVideoMuted = false;
        this.autoPlayVideo = true;
        this.loopVideo = true;
        this.videoLoaded = false;
        this.intentionallyPaused = false;
        this.system = this.owner.renderer.getSystem(TrainingVideoSystem);
    }
    onInit() {
        this.system.register(this);
    }
    setVisible(visible) {
        var _a, _b;
        if (this.mesh !== undefined) {
            this.mesh.visible = visible;
        }
        if (visible) {
            this.owner.renderer.addAnimationCallback(this.owner.modelUID, TrainingVideoFacet, () => {
                this.update();
            });
            if (!this.audioFromVideoMuted) {
                (_a = this.sound) === null || _a === void 0 ? void 0 : _a.setVolume(1);
            }
            // Resume video playback when becoming visible and autoPlay is active
            if (this.videoElement !== undefined && this.videoLoaded && this.autoPlayVideo) {
                this.intentionallyPaused = false;
                this.videoElement.play().catch(() => {
                    // Autoplay may fail, ignore
                });
            }
        }
        else {
            this.owner.renderer.removeAnimationCallback(this.owner.modelUID, TrainingVideoFacet);
            (_b = this.sound) === null || _b === void 0 ? void 0 : _b.setVolume(0);
            // Pause video when becoming invisible so the browser doesn't throttle it
            if (this.videoElement !== undefined && !this.videoElement.paused) {
                this.intentionallyPaused = true;
                this.videoElement.pause();
            }
        }
    }
    destroy() {
        this.owner.renderer.removeAnimationCallback(this.owner.modelUID, TrainingVideoFacet);
        this.cleanupVideo();
        this.system.unregister(this);
    }
    cleanupVideo() {
        var _a, _b, _c;
        if (this.videoElement !== undefined) {
            // Remove event handlers FIRST so that pause() doesn't trigger the onpause callback
            this.videoElement.onplaying = null;
            this.videoElement.onended = null;
            this.videoElement.onpause = null;
            this.videoElement.onstalled = null;
            this.videoElement.pause();
            this.videoElement.removeAttribute("src");
            this.videoElement.load(); // Release any buffered data
            if (this.videoElement.parentNode !== null) {
                this.videoElement.parentNode.removeChild(this.videoElement);
            }
            this.videoElement = undefined;
        }
        if (((_a = this.sound) === null || _a === void 0 ? void 0 : _a.isPlaying) === true) {
            this.sound.stop();
        }
        this.sound = undefined;
        (_b = this.videoTexture) === null || _b === void 0 ? void 0 : _b.dispose();
        this.videoTexture = undefined;
        (_c = this.videoTextureMaterial) === null || _c === void 0 ? void 0 : _c.dispose();
        this.videoTextureMaterial = undefined;
        if (this.videoOutline !== undefined) {
            this.videoOutline.geometry.dispose();
            if (this.videoOutline.material instanceof MeshBasicMaterial) {
                this.videoOutline.material.dispose();
            }
            this.videoOutline = undefined;
        }
        if (this.mesh !== undefined) {
            this.owner.removeObjectFromScene(this.mesh);
            this.mesh = undefined;
        }
    }
    update() {
        var _a, _b, _c;
        // Watchdog: If the video was unexpectedly paused (e.g. by browser optimization),
        // it will be automatically restarted
        if (this.videoElement !== undefined && this.videoElement.paused && !this.intentionallyPaused && this.autoPlayVideo && this.videoLoaded) {
            this.videoElement.play().catch(() => {
                // Autoplay may fail, ignore
            });
        }
        if (this.videoTexture !== undefined && this.videoElement !== undefined && this.videoElement.readyState >= this.videoElement.HAVE_CURRENT_DATA) {
            this.videoTexture.needsUpdate = true;
        }
        const cameraWorldPos = camera.perspectiveCam.getWorldPosition(new Vector3());
        (_a = this.mesh) === null || _a === void 0 ? void 0 : _a.lookAt(cameraWorldPos);
        (_b = this.mesh) === null || _b === void 0 ? void 0 : _b.position.copy(this.owner.worldPosition);
        (_c = this.mesh) === null || _c === void 0 ? void 0 : _c.updateMatrix();
    }
    updateFacet(facet) {
        return __awaiter(this, void 0, void 0, function* () {
            this.videoLoaded = false;
            this.facet = facet;
            this.bucketName = facet.bucketName;
            this.videoFileName = facet.videoFileName;
            this.autoPlayVideo = facet.autoPlay;
            this.loopVideo = facet.loop;
            this.audioFromVideoMuted = !facet.playAudio;
            // Clean up old video resources before loading a new video
            this.cleanupVideo();
            this.intentionallyPaused = !facet.autoPlay;
            this.getMeshFromOwner();
            const videoDataFetched = yield this.fetchVideo();
            if (!videoDataFetched && this.currentFacet !== undefined) {
                this.system.trainingVideoRenderables.set(this.currentFacet.id, undefined);
            }
            if (this.videoTextureMaterial !== undefined)
                this.videoTextureMaterial.needsUpdate = true;
            this.videoLoaded = true;
            this.setVisible(!facet.hidden);
        });
    }
    updateTransform() {
    }
    isVideoLoaded() {
        return this.videoLoaded;
    }
    getMeshFromOwner() {
        var _a;
        const renderable = this.owner.renderable;
        if (renderable === undefined)
            return;
        if (!isRenderableType(MeshRenderable, renderable))
            return;
        if (!renderable.meshInstance.isLoaded)
            return;
        (_a = renderable.meshInstance.group) === null || _a === void 0 ? void 0 : _a.traverse((object) => {
            if (object instanceof Mesh) {
                this.mesh = object;
            }
        });
    }
    fetchVideo() {
        return __awaiter(this, void 0, void 0, function* () {
            const videoSrc = "/api/v1/asset/" + this.videoFileName + "/" + this.bucketName + ".*";
            // Fetch or retrieve from the system-level cache to avoid re-downloading on scene reload
            const blobUrl = yield this.system.getOrFetchVideo(videoSrc);
            if (blobUrl === undefined)
                return false;
            this.videoElement = document.createElement("video");
            this.videoElement.id = this.videoFileName;
            this.videoElement.src = blobUrl;
            this.videoElement.muted = true; // Start muted to allow autoplay
            this.videoElement.playsInline = true;
            this.videoElement.preload = "auto";
            this.videoElement.style.display = "none";
            document.body.appendChild(this.videoElement);
            this.sound = new Audio(camera.audioListener);
            this.sound.setMediaElementSource(this.videoElement);
            // Set up onPlaying and onEnded handlers
            this.setVideoEventHandlers();
            // Wait until the browser has enough data to play, or reject on error
            yield new Promise((resolve, reject) => {
                if (this.videoElement === undefined) {
                    resolve();
                    return;
                }
                if (this.videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                    resolve();
                    return;
                }
                const onCanPlay = () => {
                    cleanup();
                    resolve();
                };
                const onError = () => {
                    cleanup();
                    reject(new Error(`[TrainingVideo] Could not load video: ${this.videoFileName}`));
                };
                const cleanup = () => {
                    var _a, _b;
                    (_a = this.videoElement) === null || _a === void 0 ? void 0 : _a.removeEventListener("canplay", onCanPlay);
                    (_b = this.videoElement) === null || _b === void 0 ? void 0 : _b.removeEventListener("error", onError);
                };
                this.videoElement.addEventListener("canplay", onCanPlay);
                this.videoElement.addEventListener("error", onError);
            });
            // Create VideoTexture and Mesh, but keep invisible initially
            this.createMeshWithVideoMaterial();
            // Keep mesh invisible — setVisible() in updateFacet() controls visibility
            if (this.mesh !== undefined) {
                this.mesh.visible = false;
            }
            return true;
        });
    }
    createMeshWithVideoMaterial() {
        // Create three.js VideoTexture and assign material
        if (this.videoElement === undefined)
            return;
        this.videoTexture = new VideoTexture(this.videoElement);
        this.videoTexture.colorSpace = "srgb";
        this.videoTexture.needsUpdate = true;
        this.videoTextureMaterial = new MeshBasicMaterial({ map: this.videoTexture, side: DoubleSide, depthTest: false, transparent: true });
        const aspectRatio = this.videoElement.videoWidth / this.videoElement.videoHeight;
        const videoWidth = VIDEO_HEIGHT * aspectRatio;
        const videoGeometry = this.createRoundedRectangleGeometry(videoWidth, VIDEO_HEIGHT, VIDEO_CORNER_RADIUS);
        this.mesh = new Mesh(videoGeometry, this.videoTextureMaterial);
        this.mesh.renderOrder = RenderOrder.VIEW_TO_TEXTURE;
        const outlineWidth = videoWidth + VIDEO_OUTLINE_WIDTH * 2;
        const outlineHeight = VIDEO_HEIGHT + VIDEO_OUTLINE_WIDTH * 2;
        const outlineGeometry = this.createRoundedRectangleGeometry(outlineWidth, outlineHeight, VIDEO_CORNER_RADIUS + VIDEO_OUTLINE_WIDTH);
        this.videoOutline = new Mesh(outlineGeometry, new MeshBasicMaterial({ color: VIDEO_OUTLINE_COLOR, side: DoubleSide, depthTest: false, depthWrite: false }));
        this.videoOutline.position.z = -0.001;
        this.videoOutline.renderOrder = RenderOrder.VIEW_TO_TEXTURE - 1;
        this.mesh.add(this.videoOutline);
        this.owner.addObjectToScene(this.mesh);
    }
    createRoundedRectangleGeometry(width, height, radius) {
        const geometry = new ShapeGeometry(roundedRect(-width / 2, -height / 2, width, height, radius));
        const positions = geometry.getAttribute("position");
        const uvs = geometry.getAttribute("uv");
        for (let i = 0; i < positions.count; i++) {
            uvs.setXY(i, (positions.getX(i) + width / 2) / width, (positions.getY(i) + height / 2) / height);
        }
        return geometry;
    }
    setVideoEventHandlers() {
        if (this.videoElement === undefined)
            return;
        // onplaying: Set audio volume based on settings
        this.videoElement.onplaying = () => {
            if (this.sound !== undefined) {
                this.sound.setVolume(this.audioFromVideoMuted ? 0 : 1);
                // Only unmute if the AudioContext is active (user has already interacted),
                // otherwise the browser may pause the video immediately
                if (this.videoElement !== undefined && camera.audioListener.context.state === "running") {
                    this.videoElement.muted = false;
                }
            }
            if (this.facet !== undefined) {
                this.system.updateVideoState(this.facet.id, "play");
            }
        };
        // onpause: If the video is unexpectedly paused, restart it
        this.videoElement.onpause = () => {
            if (!this.intentionallyPaused && this.autoPlayVideo && this.videoLoaded && this.videoElement !== undefined && !this.videoElement.ended) {
                this.videoElement.play().catch(() => {
                    // Autoplay may fail, ignore
                });
            }
        };
        // onstalled: Video data flow was interrupted, attempt to resume
        this.videoElement.onstalled = () => {
            if (this.videoElement !== undefined && !this.videoElement.paused) {
                console.warn("[TrainingVideo] Video stalled, attempting to resume:", this.videoFileName);
                const currentTime = this.videoElement.currentTime;
                this.videoElement.currentTime = currentTime;
            }
        };
        // onended: Restart video if looping is enabled
        this.videoElement.onended = () => __awaiter(this, void 0, void 0, function* () {
            if (this.videoElement !== undefined) {
                this.videoElement.muted = true;
                if (this.loopVideo) {
                    yield this.videoElement.play();
                }
            }
            if (this.facet !== undefined) {
                this.system.updateVideoState(this.facet.id, "ended");
            }
        });
    }
    // Playback control methods
    play() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.intentionallyPaused = false;
            yield ((_a = this.videoElement) === null || _a === void 0 ? void 0 : _a.play());
        });
    }
    pause() {
        var _a;
        this.intentionallyPaused = true;
        (_a = this.videoElement) === null || _a === void 0 ? void 0 : _a.pause();
    }
    stop() {
        this.intentionallyPaused = true;
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.currentTime = 0;
        }
    }
}
TrainingVideoRenderable.typestatic = "TrainingVideo";
